"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.renderProjectSection = renderProjectSection;
var _litHtml = require("lit-html");
var _texts = require("../../texts");
var _ProjectEditDialog = require("./ProjectEditDialog");
var _mutations = require("./mutations");
var _profileIcons = require("../../icons-svg/profileIcons");
require("../../styles/ProjectsCard.css");
var _collapsibleSection = require("../shared/collapsibleSection");
const MAX_VISIBLE_PROJECTS_MOBILE = 2;
function toggleProjectsMobileList(event) {
  const button = event.currentTarget;
  const section = button?.closest('.profile-section-collapsible');
  if (!button || !section) return;
  const nextExpanded = section.getAttribute('data-mobile-expanded') !== 'true';
  section.setAttribute('data-mobile-expanded', String(nextExpanded));
  button.setAttribute('aria-expanded', String(nextExpanded));
  button.setAttribute('data-mobile-expanded', String(nextExpanded));
  const label = button.querySelector('.project-card__more-label');
  if (label) {
    label.textContent = nextExpanded ? 'View Less' : 'View More';
  }
}
function renderProjectImage(src, altText) {
  return src ? (0, _litHtml.html)`
        <img
          class="project-card__thumb-image"
          src=${src}
          alt=${altText}
          loading="lazy"
        />
      ` : (0, _litHtml.html)`
        <div class="project-card__thumb-fallback flex-center" role="img" aria-label=${altText} tabindex="0">
          ${altText}
        </div>
      `;
}
function toProjectRow(project, status) {
  return {
    url: project.url,
    title: project.title,
    imageUrl: project.imageUrl,
    category: project.category,
    name: project.name,
    orgName: project.orgName,
    entryNode: project.entryNode?.value || '',
    status
  };
}
function renderProject(project, store, subject, viewerMode, onSaved) {
  if (!project) return (0, _litHtml.html)``;
  const handleUnfollow = async event => {
    event.preventDefault();
    if (viewerMode !== 'owner') return;
    const removePlan = {
      create: [],
      update: [],
      remove: [toProjectRow(project, 'deleted')]
    };
    await (0, _mutations.processProjectsMutations)(store, subject, removePlan);
    if (onSaved) {
      await onSaved();
    }
  };
  const normalizedCategory = (project.category || '').trim().toLowerCase();
  const categoryModifier = normalizedCategory === 'project' ? 'project' : normalizedCategory === 'community' ? 'community' : 'unknown';
  const categoryLabel = normalizedCategory && normalizedCategory !== 'unknown' ? normalizedCategory.charAt(0).toUpperCase() + normalizedCategory.slice(1) : 'Uncategorized';
  return (0, _litHtml.html)`
    <li class="project-card flex-column" role="listitem">
      <a
        class="project-card__link"
        href=${project.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label=${project.title ? `Open ${project.title}` : 'Open project link'}
      >
        <div class="project-card__wrapper">
          <div class="project-card__thumb flex-center">
            ${renderProjectImage(project.imageUrl, project.title || 'Project preview')}
          </div>
          <div class="project-card__content">
            <p class="project-card__title"><strong>${project.title || project.url}</strong></p>
            <p class="project-card__organization">${project.orgName || 'Organization unknown'}</p>
            <p class="project-card__category project-card__category--${categoryModifier}">${categoryLabel}</p>
          </div>
        </div>
      </a>
      ${viewerMode === 'owner' ? (0, _litHtml.html)`
        <div class="project-card__footer">
          <button
            type="button"
            class="project-card__follow-button flex-center gap-xxs"
            aria-label="Unfollow project"
            @click=${handleUnfollow}
          >
            <span>${_profileIcons.checkMarkIcon} Following</span>
          </button>
        </div>
      ` : (0, _litHtml.html)``}
    </li>
  `;
}
function renderOwnerEmptyProjectsContent(store, subject, projectData, viewerMode, onSaved) {
  const projectDetails = projectData;
  return (0, _litHtml.html)`
    <div class="profile__empty-state-content flex-column-center" role="group" aria-label="Empty projects section">
      <h2 id="projects-heading" tabindex="-1">${_texts.projectsHeadingText}</h2>
      <p class="profile__empty-state-message">
        You haven't added any projects yet. Consider adding a project to boost your profile.
      </p>
    </div>
    <button
      type="button"
      class="profile__action-button--empty"
      aria-label="Add project details"
      @click=${event => {
    return (0, _ProjectEditDialog.createProjectsEditDialog)(event, store, subject, projectDetails, viewerMode, onSaved);
  }}
    >
      <span class="profile__action-icon" aria-hidden="true">${_profileIcons.plusDarkIcon} Add Project</span>
    </button>

  `;
}
function renderOwnerEmptyProjectSection(store, subject, projectData, viewerMode, onSaved) {
  return (0, _litHtml.html)`
    <section 
      aria-labelledby="projects-heading" 
      data-profile-section="projects"
      class="profile__section--empty border-lighter flex-column-center rounded-md gap-lg" 
      role="region"
      tabindex="-1"
    >
      ${renderOwnerEmptyProjectsContent(store, subject, projectData, viewerMode, onSaved)}
    </section>
  `;
}
function renderProjectSectionContent(projects, store, subject, viewerMode, onSaved) {
  if (!projects || !projects.length || !projects[0]) return (0, _litHtml.html)``;
  return (0, _litHtml.html)`${renderProject(projects[0], store, subject, viewerMode, onSaved)}${projects.length > 1 ? renderProjectSectionContent(projects.slice(1), store, subject, viewerMode, onSaved) : (0, _litHtml.html)``}`;
}
function renderProjectSectionDefault(store, subject, projects, viewerMode, onSaved) {
  const hasProjects = Array.isArray(projects) && projects.length > 0;
  const hiddenProjectsCount = Math.max(0, projects.length - MAX_VISIBLE_PROJECTS_MOBILE);
  const isOwner = viewerMode === 'owner';
  return (0, _litHtml.html)`
      <section
        class="profile__section border-lighter profile-section-collapsible profile-section-collapsible--inline-mobile-actions"
        aria-labelledby="projects-heading"
        role="region"
        tabindex="-1"
        data-expanded="false"
        data-mobile-expanded="${hiddenProjectsCount > 0 ? 'false' : 'true'}"
      >
        <header class="profile__section-header profile-section-collapsible__header">
          <h2 id="projects-heading">${_texts.projectsHeadingText}</h2>
          <div class="profile-section-collapsible__actions flex-column">
            ${isOwner ? (0, _litHtml.html)`
              <button
                type="button"
                class="profile__action-button profile-action-text flex-center profile-section-collapsible__edit-button"
                aria-label="Add or edit projects"
                @click=${event => {
    return (0, _ProjectEditDialog.createProjectsEditDialog)(event, store, subject, projects, viewerMode, onSaved);
  }}
              >
                <span class="profile-section-collapsible__edit-label profile__add-more-content inline-flex-row">
                  <span class="profile__add-more-icon inline-flex-row" aria-hidden="true">${_profileIcons.addIcon}</span>
                  Add More
                </span>
                <span class="profile-section-collapsible__edit-icon profile-section-collapsible__edit-icon--add profile-section-collapsible__edit-icon--projects" aria-hidden="true">${_profileIcons.plusDarkIcon}</span>
              </button>
            ` : (0, _litHtml.html)``}
            <button
              type="button"
              class="inline-flex-row"
              aria-label="Toggle projects section"
              aria-controls="projects-panel"
              aria-expanded="false"
              @click=${_collapsibleSection.toggleCollapsibleSection}
            >
              <span class="profile-section-collapsible__chevron" aria-hidden="true">⌄</span>
            </button>
          </div>
        </header>
        <div id="projects-panel" class="profile-section-collapsible__content" aria-hidden="true">
          ${hasProjects ? (0, _litHtml.html)`
                <ul id="projects-rail" class="project-card__rail" role="list" aria-label="Known projects">
                  ${renderProjectSectionContent(projects, store, subject, viewerMode, onSaved)}
                </ul>
                ${hiddenProjectsCount > 0 ? (0, _litHtml.html)`
                      <button
                        type="button"
                        class="project-card__more-button"
                        aria-controls="projects-rail"
                        aria-expanded="false"
                        data-mobile-expanded="false"
                        @click=${toggleProjectsMobileList}
                      >
                        <span class="project-card__more-icon" aria-hidden="true">${_profileIcons.twoDownArrowsIcon}</span>
                        <span class="project-card__more-label">View More</span>
                      </button>
                    ` : (0, _litHtml.html)``}
              ` : (0, _litHtml.html)`<p>No projects added yet.</p>`}
        </div>
      </section>
    `;
}
function renderProjectSection(store, subject, projects, viewerMode, onSaved) {
  const hasProjects = Array.isArray(projects) && projects.length > 0;
  const showOwnerEmptyProject = !hasProjects && viewerMode === 'owner';
  const showSection = true;
  return showSection ? (0, _litHtml.html)`
    ${showOwnerEmptyProject ? renderOwnerEmptyProjectSection(store, subject, projects, viewerMode, onSaved) : renderProjectSectionDefault(store, subject, projects, viewerMode, onSaved)}
  ` : '';
}